import { HttpStatusCode } from "axios";
import { IcmIncidentDetailsV2, IcmSearchIncidentDetails, useORPCClient } from "Core/oRPC";
import { IcmIncidentSeverity } from "Enums/IcmIncidentSeverity";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { MaxSearchResultCount, universalSearchResultItem, useUniversalSearch } from "Shared/UniversalSearch";
import { isServiceErrorDetails } from "Shared/Utils";
import { AppRegistry } from "Apps/AppRegistry";

export const useIncidentUniversalSearch = () => {
    const { icmIncidentApi } = useORPCClient();
    const navigate = useNavigate();

    const onSearch = useCallback(
        async (searchText: string) => {
            if (!searchText) {
                return [];
            }
            const resultList: universalSearchResultItem[] = [];

            // 1. Search by incident ID
            if (/^\d+$/.test(searchText)) {
                try {
                    const incidentDetail = await icmIncidentApi.getIncidentDetail({ id: searchText });
                    resultList.push(getUniversalSearchResultItem(incidentDetail));
                } catch (error) {
                    // If the incident ID is not found, the API will return 404.
                    if (isServiceErrorDetails(error) && error.StatusCode !== HttpStatusCode.NotFound) {
                        throw error;
                    }
                }
            }

            const incidentList = await icmIncidentApi.postSearchIncidentsByPage({
                body: {
                    SearchText: searchText,
                    EndTime: DateTime.utc().toISO()
                },
                query: { requestCount: MaxSearchResultCount }
            });
            resultList.push(...incidentList.map((item) => getUniversalSearchResultItem(item)));

            return resultList.slice(0, MaxSearchResultCount).map((item) => ({
                ...item,
                onClick: () => navigate(`/incidents/${item.id}/details`),
                navigationTarget: `/incidents/${item.id}/details`
            }));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate]
    );

    const { searchWord } = useUniversalSearch("Incidents", "incident ID or incident Description", onSearch, false, AppRegistry.Incidents.path);
    return { word: searchWord };
};

function getUniversalSearchResultItem(incident: IcmIncidentDetailsV2 | IcmSearchIncidentDetails) {
    const item: universalSearchResultItem = {
        id: incident.Id?.toString() ?? "",
        title: `${incident.State} ${incident.Severity && incident.Severity in IcmIncidentSeverity ? IcmIncidentSeverity[incident.Severity] : ""} ${
            incident.Id
        }`,
        description: incident.Title ?? ""
    };
    return item;
}
